import React, { createContext, useContext, useEffect, useState } from 'react';
import cookie from 'js-cookie';

import { logger } from 'Services/logger/logger';
import { isProd } from 'Utilities/helpers';
import useUser from 'Utilities/hooks/useUser';

interface DriverPinContextType {
	driverPin: number | null;
	isPending: boolean;
}

const DriverPinContext = createContext<DriverPinContextType | undefined>(undefined);

export const DriverPinProvider: React.FC<{
	children: React.ReactNode;
}> = ({ children }) => {
	const [driverPin, setDriverPin] = useState<number | null>(null);
	const [isPending, setIsPending] = useState(true);

	const user = useUser();
	const { dealerId } = user ?? {};

	useEffect(() => {
		const token = cookie.get('access-token');

		if (!dealerId || !token) {
			setIsPending(false);
			return;
		}

		const getPin = async () => {
			const domain = isProd ? '' : 'stage.';

			const fetchedPin = await fetch(
				`https://api.${domain}motorway.co.uk/sale-finalisation/handshake/dealer-config/${dealerId}`,
				{
					headers: {
						'Content-Type': 'application/json',
						...(token && { 'x-mway-authorization': token }),
					},
					signal: AbortSignal.timeout(5000),
				},
			)
				.then((res) => res.json())
				.then((data) => {
					const { pin } = data ?? {};
					return pin;
				})
				.catch((error) => {
					logger.error({
						error,
						message: 'Failed to fetch driver PIN',
						scope: 'DriverPinProvider',
					});
					return null;
				});

			setIsPending(false);
			setDriverPin(fetchedPin);
		};

		void getPin();
	}, [dealerId]);

	return <DriverPinContext.Provider value={{ driverPin, isPending }}>{children}</DriverPinContext.Provider>;
};

export const useDriverPin = (): DriverPinContextType => {
	const context = useContext(DriverPinContext);

	return context ?? { driverPin: null, isPending: false };
};
